/**
 * chatStream machine — domain types and named action implementations.
 *
 * `ChatStreamContext`, `ChatStreamEvent`, and `ChatStreamInput` live here so
 * that this file has no dependency on `chatStreamMachine.ts` — avoiding a
 * circular module graph. `chatStreamMachine.ts` imports everything it needs
 * (types + actions) from here and re-exports the types for external callers.
 */

import type { ChunkBuffer } from 'src/content/ChatV2/stream/ChunkBuffer';
import type { ChatMetaEventPayload } from 'src/content/ChatV2/transport/eventSchema';
import type { ChatError } from 'src/content/ChatV2/types/errors';
import type { ChatId, CorrelationId } from 'src/content/ChatV2/types/ids';

/**
 * The chat FSM's EVENT vocabulary.
 */
export enum CHAT_STREAM_EVENT {
  START = 'START',
  CONNECTED = 'CONNECTED',
  STREAM_CHUNK = 'STREAM_CHUNK',
  STREAM_DONE = 'STREAM_DONE',
  STREAM_ERROR = 'STREAM_ERROR',
  TURN_PERSISTED = 'TURN_PERSISTED',
  INTERRUPT = 'INTERRUPT',
  ABORT = 'ABORT',
  STREAM_CANCELLED = 'STREAM_CANCELLED',
  RETRY = 'RETRY',
  RESET = 'RESET'
}

/**
 * The chat FSM's STATE vocabulary.
 */
export enum CHAT_STREAM_STATE {
  IDLE = 'idle',
  CONNECTING = 'connecting',
  STREAMING = 'streaming',
  DONE = 'done',
  ERROR = 'error',
  CANCELLING = 'cancelling',
  ABORTED = 'aborted'
}

/**
 * "Is a turn in flight?" — the ONE definition.
 */
export function isStreamingState(state: string | undefined): boolean {
  return (
    state === CHAT_STREAM_STATE.CONNECTING ||
    state === CHAT_STREAM_STATE.STREAMING ||
    state === CHAT_STREAM_STATE.CANCELLING
  );
}

/**
 * "Has the user asked to stop, and are we still waiting?" — the affordance
 * question, distinct from `isStreamingState`'s lifecycle question. Drives the
 * composer's stop button going busy.
 */
export function isCancellingState(state: string | undefined): boolean {
  return state === CHAT_STREAM_STATE.CANCELLING;
}

/**
 * "Can the user still stop this turn?"
 */
export function isAbortableState(state: string | undefined): boolean {
  return (
    state === CHAT_STREAM_STATE.CONNECTING ||
    state === CHAT_STREAM_STATE.STREAMING
  );
}

export interface ChatStreamContext {
  chatId: ChatId;
  buffer: ChunkBuffer;
  serverChatId?: string;
  correlationId?: CorrelationId;
  error?: ChatError;
  startedAt?: number;
  meta?: ChatMetaEventPayload;
  queryClientId?: string;
  responseClientId?: string;
  queryServerId?: string;
  responseServerId?: string;
  responseModelId?: number;
}

export type ChatStreamEvent =
  | {
      type: CHAT_STREAM_EVENT.START;
      correlationId: CorrelationId;
      startedAt: number;
      queryClientId?: string;
      responseClientId?: string;
    }
  | { type: CHAT_STREAM_EVENT.CONNECTED }
  | { type: CHAT_STREAM_EVENT.STREAM_CHUNK; text: string }
  | { type: CHAT_STREAM_EVENT.STREAM_DONE; meta?: ChatMetaEventPayload }
  | { type: CHAT_STREAM_EVENT.STREAM_ERROR; error: ChatError }
  | {
      type: CHAT_STREAM_EVENT.TURN_PERSISTED;
      queryServerId?: string;
      responseServerId?: string;
      responseModelId?: number;
      serverChatId?: string;
    }
  | { type: CHAT_STREAM_EVENT.INTERRUPT }
  | { type: CHAT_STREAM_EVENT.ABORT }
  | { type: CHAT_STREAM_EVENT.STREAM_CANCELLED }
  | { type: CHAT_STREAM_EVENT.RETRY }
  | { type: CHAT_STREAM_EVENT.RESET };

export interface ChatStreamInput {
  chatId: ChatId;
  buffer: ChunkBuffer;
}

type ActionParam = { context: ChatStreamContext; event: ChatStreamEvent };

export const chatStreamEffects = {
  appendChunk: ({ context, event }: ActionParam) => {
    if (event.type !== CHAT_STREAM_EVENT.STREAM_CHUNK) {
      return;
    }
    context.buffer.append(event.text);
  },
  clearBuffer: ({ context }: ActionParam) => {
    context.buffer.reset();
  }
};

export const chatStreamAssigns = {
  rememberCorrelation: ({ event }: ActionParam) => ({
    correlationId:
      event.type === CHAT_STREAM_EVENT.START ? event.correlationId : undefined,
    startedAt:
      event.type === CHAT_STREAM_EVENT.START ? event.startedAt : undefined,
    error: undefined as ChatError | undefined,
    queryClientId:
      event.type === CHAT_STREAM_EVENT.START ? event.queryClientId : undefined,
    responseClientId:
      event.type === CHAT_STREAM_EVENT.START
        ? event.responseClientId
        : undefined,
    queryServerId: undefined as string | undefined,
    responseServerId: undefined as string | undefined,
    responseModelId: undefined as number | undefined
  }),
  rememberServerIds: ({ context, event }: ActionParam) => ({
    serverChatId:
      (event.type === CHAT_STREAM_EVENT.TURN_PERSISTED
        ? event.serverChatId
        : undefined) ?? context.serverChatId,
    queryServerId:
      event.type === CHAT_STREAM_EVENT.TURN_PERSISTED
        ? event.queryServerId
        : undefined,
    responseServerId:
      event.type === CHAT_STREAM_EVENT.TURN_PERSISTED
        ? event.responseServerId
        : undefined,
    responseModelId:
      event.type === CHAT_STREAM_EVENT.TURN_PERSISTED
        ? event.responseModelId
        : undefined
  }),
  captureError: ({ event }: ActionParam) => ({
    error:
      event.type === CHAT_STREAM_EVENT.STREAM_ERROR ? event.error : undefined
  }),
  captureMeta: ({ event }: ActionParam) => ({
    meta: event.type === CHAT_STREAM_EVENT.STREAM_DONE ? event.meta : undefined
  }),
  forgetCorrelation: (_: ActionParam) => ({
    correlationId: undefined as CorrelationId | undefined,
    startedAt: undefined as number | undefined,
    error: undefined as ChatError | undefined,
    meta: undefined as ChatMetaEventPayload | undefined,
    queryServerId: undefined as string | undefined,
    responseServerId: undefined as string | undefined
  })
};
